import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useState } from 'react';

import { Nullable } from '@common/type/CommonTypes';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogContent, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import CourseDiscussionItem, { CourseDiscussionItemType } from '../courseDiscussionItem';

import { ButtonSubmitStyle } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import TextField from '@components/atoms/textField';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';
import { numberWithCommas } from '@helpers/utils';
import {
  AddCourseDiscussionReplyPayload,
  useAddCourseDiscussionReply,
} from '@hooks/course/courseDiscussion/useAddCourseDiscussionReply';
import { useCourseDiscussionList } from '@hooks/course/courseDiscussion/useCourseDiscussionList';
import { CourseDiscussionEntity } from '@redux/course/type/courseDetailType';

const MAX_CONTENT_LENGTH = 3000;

type AddCourseDiscussinoReplyForm = Omit<AddCourseDiscussionReplyPayload, 'discussionId'>;

type ReplyDiscussionDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedDiscussion: Nullable<CourseDiscussionEntity>;
  searchQuery: string;
};

const ReplyDiscussionDialog: FC<ReplyDiscussionDialogProps> = ({
  isOpen,
  setIsOpen,
  selectedDiscussion,
  searchQuery,
}) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const router = useRouter();
  const idName = router.query.idName as string;

  const {
    handleAddCourseDiscussionReply,
    isAddCourseDiscussionReplyDataLoading,
    isAddCourseDiscussionReplyDataSuccess,
    addCourseDiscussionReplyStateData,
    handleResetCourseDiscussionReply,
  } = useAddCourseDiscussionReply({
    idName,
  });

  const { handleFetchCourseDiscussionList, courseDiscussionListPage } = useCourseDiscussionList({ idName });

  const [discussion, setDiscussion] = useState<Nullable<CourseDiscussionEntity>>(null);
  const [isShowReply, setIsShowReply] = useState<boolean>(false);

  const replyDiscussionSchemaValidate = yup.object({
    content: yup
      .string()
      .test('validate length', 'error', (value) => (value || '').length <= MAX_CONTENT_LENGTH)
      .required(),
  });

  const replyDiscussionForm = useForm<AddCourseDiscussinoReplyForm>({
    defaultValues: {
      content: '',
    },
    mode: 'onChange',
    resolver: yupResolver(replyDiscussionSchemaValidate),
  });

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
    handleSubmit,
  } = replyDiscussionForm;

  const contentValue = watch('content');

  const isDisabledSubmit = useMemo(() => {
    return isAddCourseDiscussionReplyDataLoading || !!errors?.content || !contentValue;
  }, [isAddCourseDiscussionReplyDataLoading, errors?.content, contentValue]);

  const clearState = () => {
    setValue('content', '');
    clearErrors();
  };

  useEffect(() => {
    if (isOpen && selectedDiscussion) {
      setDiscussion({ ...selectedDiscussion });
    }
    if (!isOpen) {
      setDiscussion(null);
    }
  }, [isOpen, selectedDiscussion]);

  useEffect(() => {
    if (isAddCourseDiscussionReplyDataSuccess) {
      if (discussion) {
        discussion.discussionReplies.push(addCourseDiscussionReplyStateData);
        setDiscussion({ ...discussion });
      }
      clearState();
      handleFetchCourseDiscussionList({ page: courseDiscussionListPage, title: searchQuery });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddCourseDiscussionReplyDataSuccess]);

  const handleClose = () => {
    setIsOpen(false);
    setIsShowReply(false);
    clearState();
    handleResetCourseDiscussionReply();
  };

  return (
    <BaseModalWrapper
      open={isOpen}
      onClose={handleClose}
      maxWidth={750}
      paperStyle={{ width: 750, py: 0, maxHeight: { xs: 'calc(100vh - 48px)', md: 'calc(100vh - 96px)' } }}
      boxStyle={{ maxHeight: { xs: 'calc(100vh - 48px)', md: 'calc(100vh - 96px)' }, overflow: 'hidden' }}
      disabledBackDrop
    >
      <DialogContent
        sx={{
          maxHeight: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          py: { xs: 5, md: 8 },
          px: { xs: 1, md: 3 },
        }}
      >
        <Box sx={{ overflowY: 'auto' }}>
          {discussion ? (
            <>
              <Box mb={1}>
                <CourseDiscussionItem
                  type={CourseDiscussionItemType.FULL_INFO}
                  imageUrl={discussion.user?.imageUrl || ''}
                  fullName={discussion.user?.fullname}
                  content={discussion.title}
                  createdAt={discussion.createdAt}
                  isAuthor={discussion.user?.isAuthor}
                />
              </Box>
              <Box ml={6}>
                {isShowReply
                  ? discussion.discussionReplies.map((discussionReply) => (
                      <Box key={discussionReply.id} mb={1}>
                        <CourseDiscussionItem
                          type={CourseDiscussionItemType.REPLY}
                          imageUrl={discussionReply.user?.imageUrl || ''}
                          fullName={discussionReply.user?.fullname}
                          content={discussionReply.content}
                          createdAt={discussionReply.createdAt}
                          isAuthor={discussionReply.user?.isAuthor}
                        />
                      </Box>
                    ))
                  : null}

                {discussion.discussionReplies.length > 0 && (
                  <Button
                    onClick={() => setIsShowReply(!isShowReply)}
                    variant="text"
                    sx={{
                      color: theme.palette.primary.main,
                      minWidth: 48,
                      maxHeight: 26,
                      py: 0,
                      px: 1,
                      display: 'flex',
                      gap: 1,
                    }}
                    disableRipple
                  >
                    <FontAwesomeIcon
                      icon={`fa-light ${isShowReply ? 'fa-arrow-turn-up' : 'fa-arrow-turn-down-right'}`}
                      width={16}
                    />
                    {t(
                      isShowReply
                        ? 'course_detail.course_discussion.reply_modal.hide_reply_list'
                        : 'course_detail.course_discussion.reply_modal.reply_list',
                      { replyCount: discussion.discussionReplies.length },
                    )}
                  </Button>
                )}
              </Box>
            </>
          ) : null}

          <Box ml={6} py={1}>
            <Controller
              name="content"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  multiline
                  value={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  placeholder={t('course_detail.course_discussion.reply_modal.reply')}
                  InputProps={{
                    sx: { minHeight: 38, fontSize: 18, lineHeight: '22px', p: '0 0 0 9px' },
                    endAdornment: (
                      <ButtonSubmitStyle
                        disabled={isDisabledSubmit}
                        variant="text"
                        type="submit"
                        onClick={handleSubmit((values) =>
                          handleAddCourseDiscussionReply({ ...values, discussionId: Number(discussion?.id) }),
                        )}
                        sx={{
                          minWidth: 50,
                          color: isDisabledSubmit ? theme.palette.textV2.disabled : theme.palette.primaryV2.main,
                          mt: 'auto',
                        }}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-paper-plane-top" width={18} />
                      </ButtonSubmitStyle>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>
        <Box display="flex" justifyContent="end" alignItems="start" gap="2px">
          <Typography
            variant="helperText"
            color={errors.content?.message ? theme.palette.errorV2.main : theme.palette.textV2.secondary}
          >
            {numberWithCommas((contentValue || '').length)}
          </Typography>
          <Typography variant="helperText" color={theme.palette.textV2.secondary}>
            /
          </Typography>
          <Typography variant="helperText" color={theme.palette.textV2.secondary}>
            {numberWithCommas(MAX_CONTENT_LENGTH)}
          </Typography>
        </Box>
      </DialogContent>
    </BaseModalWrapper>
  );
};

export default ReplyDiscussionDialog;
